import { useEffect, useMemo, useState } from 'react'
import { AlertCircle, Bell, Check, Plus, Save, Trash2 } from 'lucide-react'

import { supabase } from '../lib/supabase'
import { formatModelLabel, normalizeModelId } from '../lib/modelLabel'
import { Button } from './ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { Switch } from './ui/switch'
import { TeamRole } from '../types/supabase'

type Channel = 'slack' | 'email' | 'sms'
type RuleType = 'exact_model' | 'new_model'

interface ModelRule {
  id?: string
  rule_type: RuleType
  model_id: string | null
  enabled: boolean
  notify_admins: boolean
  notify_managers: boolean
  channels: Channel[]
  daily_summary_enabled: boolean
}

interface ModelUsageNotificationConfigurationProps {
  teamId: string
  userRole: TeamRole
}

const defaultRule = (modelId: string): ModelRule => ({
  rule_type: 'exact_model',
  model_id: modelId,
  enabled: true,
  notify_admins: true,
  notify_managers: true,
  channels: ['slack'],
  daily_summary_enabled: false,
})

const defaultNewModelRule = (): ModelRule => ({
  rule_type: 'new_model',
  model_id: null,
  enabled: false,
  notify_admins: true,
  notify_managers: true,
  channels: ['slack'],
  daily_summary_enabled: false,
})

export function ModelUsageNotificationConfiguration({ teamId, userRole }: ModelUsageNotificationConfigurationProps) {
  const [rules, setRules] = useState<ModelRule[]>([])
  const [catalogModels, setCatalogModels] = useState<string[]>([])
  const [observedModels, setObservedModels] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const isAdmin = userRole === 'admin' || userRole === 'owner'
  const availableModels = useMemo(
    () => Array.from(new Set([...catalogModels, ...observedModels])).sort(),
    [catalogModels, observedModels],
  )
  const knownModels = useMemo(() => new Set(catalogModels), [catalogModels])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError('')
      try {
        const [{ data: loadedRules, error: rulesError }, { data: pricingRows, error: pricingError }, { data: eventRows, error: eventsError }] = await Promise.all([
          (supabase as any).from('model_usage_notification_rules').select('id, rule_type, model_id, enabled, notify_admins, notify_managers, channels, daily_summary_enabled').eq('team_id', teamId).order('created_at'),
          (supabase as any).from('model_pricing').select('model_id').order('model_id'),
          (supabase as any).from('agent_usage_events').select('model').eq('team_id', teamId).order('occurred_at', { ascending: false }).limit(250),
        ])
        if (rulesError) throw rulesError
        if (pricingError) throw pricingError
        if (eventsError) throw eventsError
        if (cancelled) return

        const catalog = Array.from(new Set((pricingRows ?? []).map((row: { model_id: string }) => normalizeModelId(row.model_id)).filter(Boolean))) as string[]
        const observed = Array.from(new Set((eventRows ?? []).map((row: { model: string | null }) => normalizeModelId(row.model)).filter(Boolean))) as string[]
        const normalizedRules: ModelRule[] = ((loadedRules ?? []) as any[]).map((row: any): ModelRule => ({
          id: row.id,
          rule_type: row.rule_type,
          model_id: row.model_id,
          enabled: row.enabled,
          notify_admins: row.notify_admins,
          notify_managers: row.notify_managers,
          channels: (row.channels ?? ['slack']).filter((channel: string): channel is Channel => channel === 'slack' || channel === 'email' || channel === 'sms'),
          daily_summary_enabled: row.daily_summary_enabled,
        }))
        if (!normalizedRules.some((rule) => rule.rule_type === 'new_model')) normalizedRules.unshift(defaultNewModelRule())
        setCatalogModels(catalog)
        setObservedModels(observed)
        setRules(normalizedRules)
      } catch (loadError: any) {
        if (!cancelled) setError(loadError.message || 'Failed to load model notification settings')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [teamId])

  const updateRule = (index: number, patch: Partial<ModelRule>) => {
    setRules((current) => current.map((rule, ruleIndex) => ruleIndex === index ? { ...rule, ...patch } : rule))
    setSuccess('')
  }

  const addRule = () => {
    const selected = new Set(rules.filter((rule) => rule.rule_type === 'exact_model').map((rule) => rule.model_id))
    const nextModel = availableModels.find((model) => !selected.has(model))
    if (nextModel) setRules((current) => [...current, defaultRule(nextModel)])
  }

  const removeRule = (index: number) => {
    if (rules[index]?.rule_type === 'new_model') return
    setRules((current) => current.filter((_, ruleIndex) => ruleIndex !== index))
  }

  const save = async () => {
    if (!isAdmin) return
    setSaving(true)
    setError('')
    setSuccess('')
    try {
      const exactRules = rules.filter((rule) => rule.rule_type === 'exact_model' && rule.model_id)
      const newModelRule = rules.find((rule) => rule.rule_type === 'new_model') ?? defaultNewModelRule()
      const { data: existing, error: existingError } = await (supabase as any).from('model_usage_notification_rules').select('id').eq('team_id', teamId)
      if (existingError) throw existingError
      const currentIds = new Set(rules.map((rule) => rule.id).filter(Boolean))
      for (const existingRule of existing ?? []) {
        if (!currentIds.has(existingRule.id)) {
          const { error: deleteError } = await (supabase as any).from('model_usage_notification_rules').delete().eq('id', existingRule.id).eq('team_id', teamId)
          if (deleteError) throw deleteError
        }
      }
      const rows = [...exactRules, newModelRule].map((rule) => ({
        ...(rule.id ? { id: rule.id } : {}),
        team_id: teamId,
        rule_type: rule.rule_type,
        model_id: rule.model_id,
        enabled: rule.enabled,
        notify_admins: rule.notify_admins,
        notify_managers: rule.notify_managers,
        channels: rule.channels.length ? rule.channels : ['slack'],
        daily_summary_enabled: rule.daily_summary_enabled,
      }))
      const savedRows: any[] = []
      for (const row of rows) {
        const { id, ...values } = row
        const query = id
          ? (supabase as any).from('model_usage_notification_rules').update(values).eq('id', id).eq('team_id', teamId)
          : (supabase as any).from('model_usage_notification_rules').insert(values)
        const { data: saved, error: saveError } = await query
          .select('id, rule_type, model_id, enabled, notify_admins, notify_managers, channels, daily_summary_enabled')
          .single()
        if (saveError) throw saveError
        savedRows.push(saved)
      }
      setRules((savedRows ?? rows).map((row: any): ModelRule => ({
        id: row.id,
        rule_type: row.rule_type,
        model_id: row.model_id,
        enabled: row.enabled,
        notify_admins: row.notify_admins,
        notify_managers: row.notify_managers,
        channels: (row.channels ?? ['slack']).filter((channel: string): channel is Channel => channel === 'slack' || channel === 'email' || channel === 'sms'),
        daily_summary_enabled: row.daily_summary_enabled,
      })))
      setSuccess('Model notification settings saved.')
    } catch (saveError: any) {
      setError(saveError.message || 'Failed to save model notification settings')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Loading model notification settings…</div>

  return (
    <div className="bg-background">
      <div className="border-b border-border p-4 sm:p-6">
        <div className="flex items-start gap-3">
          <Bell className="mt-0.5 h-5 w-5 text-primary" />
          <div>
            <h2 className="text-lg font-semibold text-foreground">Model usage notifications</h2>
            <p className="mt-1 text-sm text-muted-foreground">Notify admins or managers when someone first uses a selected model each day. Later usage is aggregated.</p>
          </div>
        </div>
      </div>
      {error && <div role="alert" className="flex items-center gap-2 border-b border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"><AlertCircle className="h-4 w-4" />{error}</div>}
      {success && <div role="status" className="flex items-center gap-2 border-b border-primary/30 bg-primary/10 px-4 py-3 text-sm"><Check className="h-4 w-4" />{success}</div>}
      <div className="space-y-6 p-4 sm:p-6">
        <section className="space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-foreground">New or unrecognized models</h3>
            <p className="mt-1 text-xs text-muted-foreground">Catch a newly released model such as Fable 6 before it is added to the catalog.</p>
          </div>
          {rules.map((rule, index) => rule.rule_type === 'new_model' && (
            <div key={rule.id ?? 'new-model'} className="space-y-3 border border-border p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-medium">Notify on new models</div>
                <Switch checked={rule.enabled} onCheckedChange={(enabled) => updateRule(index, { enabled })} disabled={!isAdmin} aria-label="Notify on new models" />
              </div>
              {rule.enabled && <RuleOptions rule={rule} onChange={(patch) => updateRule(index, patch)} disabled={!isAdmin} />}
            </div>
          ))}
        </section>

        <section className="space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Selected models</h3>
              <p className="mt-1 text-xs text-muted-foreground">Choose the models that should create a first-use notification for each person.</p>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={addRule} disabled={!isAdmin || !availableModels.some((model) => !rules.some((rule) => rule.model_id === model))}><Plus className="mr-2 h-4 w-4" />Add model</Button>
          </div>
          <div className="divide-y divide-border border-y border-border">
            {rules.map((rule, index) => rule.rule_type === 'exact_model' && (
              <div key={rule.id ?? `model-${index}`} className="space-y-3 py-4">
                <div className="flex items-center gap-2">
                  <Select value={rule.model_id ?? ''} onValueChange={(model_id) => updateRule(index, { model_id })} disabled={!isAdmin}>
                    <SelectTrigger aria-label="Model" className="flex-1"><SelectValue placeholder="Select a model" /></SelectTrigger>
                    <SelectContent>{availableModels.map((model) => <SelectItem key={model} value={model}>{formatModelLabel(model)}{!knownModels.has(model) ? ' · recently observed' : ''}</SelectItem>)}</SelectContent>
                  </Select>
                  <Switch checked={rule.enabled} onCheckedChange={(enabled) => updateRule(index, { enabled })} disabled={!isAdmin} aria-label="Enable model notification" />
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeRule(index)} disabled={!isAdmin} aria-label="Remove model notification"><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
                </div>
                {rule.enabled && <RuleOptions rule={rule} onChange={(patch) => updateRule(index, patch)} disabled={!isAdmin} />}
              </div>
            ))}
            {!rules.some((rule) => rule.rule_type === 'exact_model') && <p className="py-8 text-sm text-muted-foreground">No selected models yet.</p>}
          </div>
        </section>

        {observedModels.some((model) => !knownModels.has(model)) && <p className="text-xs text-amber-700 dark:text-amber-300">Recently observed models not yet in the pricing catalog are shown in the selector and can be covered by the new-model rule.</p>}
      </div>
      <div className="flex justify-end border-t border-border p-4 sm:p-6"><Button onClick={() => void save()} disabled={saving || !isAdmin}>{saving ? 'Saving…' : 'Save changes'}</Button></div>
    </div>
  )
}

function RuleOptions({ rule, onChange, disabled }: { rule: ModelRule; onChange: (patch: Partial<ModelRule>) => void; disabled: boolean }) {
  const toggleChannel = (channel: Channel) => {
    const channels = rule.channels.includes(channel) ? rule.channels.filter((value) => value !== channel) : [...rule.channels, channel]
    onChange({ channels: channels.length ? channels : ['slack'] })
  }
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-3 text-xs text-muted-foreground">
      <label className="flex items-center gap-2"><Switch className="scale-75" checked={rule.notify_admins} onCheckedChange={(notify_admins) => onChange({ notify_admins })} disabled={disabled} />Admins</label>
      <label className="flex items-center gap-2"><Switch className="scale-75" checked={rule.notify_managers} onCheckedChange={(notify_managers) => onChange({ notify_managers })} disabled={disabled} />Managers</label>
      <label className="flex items-center gap-2"><Switch className="scale-75" checked={rule.daily_summary_enabled} onCheckedChange={(daily_summary_enabled) => onChange({ daily_summary_enabled })} disabled={disabled} />Daily summary</label>
      <span className="font-medium text-foreground">Channels:</span>
      <label className="flex items-center gap-2"><Switch className="scale-75" checked={rule.channels.includes('slack')} onCheckedChange={() => toggleChannel('slack')} disabled={disabled} />Slack</label>
      <label className="flex items-center gap-2"><Switch className="scale-75" checked={rule.channels.includes('email')} onCheckedChange={() => toggleChannel('email')} disabled={disabled} />Email</label>
      <label className="flex items-center gap-2"><Switch className="scale-75" checked={rule.channels.includes('sms')} onCheckedChange={() => toggleChannel('sms')} disabled={disabled} />SMS</label>
    </div>
  )
}
